import { useEffect, useState } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { getDefaultUserRole } from '../utils/userRoles';

interface GoogleMailIntegration {
  accessToken?: string;
  refreshToken?: string;
  email?: string;
  expiryDate?: number;
  pushToken?: string;
}

interface BudgetData {
  yearlyBudget?: number;
  availableBudget?: number;
}

interface UserProfile {
  user: User | null;
  email: string | null;
  language: string;
  loading: boolean;
  pushToken?: string;
  googleMailIntegration?: GoogleMailIntegration;
  darkMode: boolean;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  role?: string;
  status?: 'invited' | 'active' | 'blocked' | 'archive';
  company?: string;
  budgetId?: string;
  budget?: {
    [year: string]: BudgetData;
  };
}

export const useUserProfile = (): UserProfile => {
  const [user, setUser] = useState<User | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [language, setLanguage] = useState<string>('en');
  const [pushToken, setPushToken] = useState<string | undefined>();
  const [googleMailIntegration, setGoogleMailIntegration] = useState<GoogleMailIntegration>();
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    // Initialize from localStorage first to prevent flash
    const saved = localStorage.getItem('theme');
    if (saved === 'dark') {
      return true;
    }
    if (saved === 'light') {
      return false;
    }
    // Fallback to system preference
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return systemPrefersDark;
  });
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [avatar, setAvatar] = useState<string>('');
  const [role, setRole] = useState<string>('');
  const [status, setStatus] = useState<'invited' | 'active' | 'blocked' | 'archive'>('active');
  const [company, setCompany] = useState<string>('');
  const [budgetId, setBudgetId] = useState<string | undefined>();
  const [budget, setBudget] = useState<{ [year: string]: BudgetData } | undefined>();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let unsubscribeDoc: (() => void) | undefined;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setEmail(null);
        setLanguage('en');
        setPushToken(undefined);
        setGoogleMailIntegration(undefined);
        // Don't reset darkMode on logout - keep user's preference
        setFirstName('');
        setLastName('');
        setAvatar('');
        setRole('');
        setStatus('active');
        setCompany('');
        setBudgetId(undefined);
        setBudget(undefined);
        setLoading(false);
        return;
      }

      setUser(firebaseUser);
      setEmail(firebaseUser.email ?? null);

      try {
        // Set up real-time listener for user document
        const userRef = doc(db, 'users', firebaseUser.uid);
        unsubscribeDoc = onSnapshot(userRef, (doc) => {
          if (doc.exists()) {
            const data = doc.data();


            setLanguage(data.language || 'en');
            setPushToken(data.pushToken);
            setGoogleMailIntegration(data.mailIntegration?.google);
            
            // Only update darkMode if it's explicitly set in Firestore
            // This prevents resetting to false when the document loads
            if (typeof data.darkMode === 'boolean') {
              setDarkMode(data.darkMode);
              // Update localStorage to match Firestore
              localStorage.setItem('theme', data.darkMode ? 'dark' : 'light');
            }
            
            setBudgetId(data.budgetId);
            setBudget(data.budget);
            // Handle different possible field names for first and last name
            const firstNameValue = data.firstName || data.first_name || data.givenName || data.given_name || '';
            const lastNameValue = data.lastName || data.last_name || data.familyName || data.family_name || '';

            setFirstName(firstNameValue);
            setLastName(lastNameValue);
            setAvatar(data.avatar || '');
            setRole(data.role || getDefaultUserRole());
            setStatus(data.status || 'active');
            setCompany(data.company || '');
          }
        }, (error) => {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Failed to listen to user document:', error);
          }
        });
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Failed to fetch user profile:', error);
        }
      }

      setLoading(false);
    });

    // Clean up both listeners when component unmounts
    return () => {
      unsubscribe();
      if (unsubscribeDoc) {
        unsubscribeDoc();
      }
    };
  }, []);

  return { user, email, language, loading, pushToken, googleMailIntegration, darkMode, firstName, lastName, avatar, role, status, company, budgetId, budget };
};
